import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql, Link } from 'gatsby';
import { useQuery } from '@apollo/client';
import { useIsClient } from '@uidotdev/usehooks';
import Countdown from 'react-countdown';
import { Carousel, Column, Container, Row } from '@pella/sash-framework';
import { CTA, Image, SEO } from '../../components';
import { useBranchId } from '../../hooks/use-branch-id';
import { useBranchData } from '../../hooks/use-branch-data';
import { useRouteChange } from '../../hooks/use-route-change';
import { useBranchShowrooms } from '../../hooks/use-branch-showrooms';
import { isValidArray } from '../../components/utilities/validations';
import { GET_PROMOS_BY_BRANCH } from '../../enums/graphql';
import { formatPhoneNumber, getContrastColor } from '../../components/utilities/functions';
import { useLivePreview } from '../../hooks/use-live-preview';

import Logo from '../../components/common/logo';
import ProductStyleTabs from '../../components/template/branch-sem/product-style-tabs';
import NearbyShowrooms from '../../components/template/branch-showroom/nearby-showrooms';
import BranchOfferText from '../../components/common/branch-offer/branch-offer-text';
import ScheduleConsultation from '../../components/page/rta/v3/home';

const SAPaidLanding = (props) => {
	const pageData = useLivePreview(props.data.contentstackSaPaidLanding, 'sa_paid_landing');

	const isClient = useIsClient();
	const branchId = useBranchId();
	const branchesData = useBranchData();
	const [branchData, setBranchData] = useState({});
	const { showrooms, error } = useBranchShowrooms([branchId], null, branchData?.additional_showrooms);
	const { data } = useQuery(GET_PROMOS_BY_BRANCH, { variables: { branchIDs: branchId }, skip: !branchId });
	const phoneNumber = '8334046788';

	useEffect(() => {
		if (!branchId) return;

		const currentBranch = branchesData?.find((data) => data.branch_retail?.includes(branchId));
		setBranchData(currentBranch);
	}, [branchId, branchesData]);

	useRouteChange({
		pageTitle: pageData.seo?.title,
		pageType: '',
		branchID: branchId,
	});

	const checkDisclaimerDuplicates = (value, claim) => {
		if (value === data?.getPromotionsByBranchId?.[0]?.disclaimer1) return false;
		if (claim === 3 && value === data?.getPromotionsByBranchId?.[0]?.disclaimer2) return false;

		return true;
	};

	return (
		<div
			className='sa-landing'
			id='sa-landing'
		>
			<header className='sa-header bg-white py-2 md:py-4 xl:py-6'>
				<Container className='grid grid-cols-[auto_auto_1fr] items-center'>
					<Link to='/'>
						<Logo className='h-14 md:h-20' />
					</Link>

					{pageData.page_heading && (
						<div
							{...(pageData.$?.page_heading ?? {})}
							className='text-subtitle-2 ml-2 w-36 md:ml-4'
							dangerouslySetInnerHTML={{ __html: pageData.page_heading }}
						/>
					)}

					<p className='m-0 text-right md:ml-16 md:text-left'>
						<span className='text-body-2 block'>Call Today</span>
						<span className='text-subtitle block font-bold'>
							<a
								href={`tel:${phoneNumber}`}
								className='ctPhone no-underline hover:underline'
							>
								{formatPhoneNumber(phoneNumber)}
							</a>
						</span>
					</p>
				</Container>
			</header>

			{isValidArray(pageData.modular_blocks) &&
				pageData.modular_blocks.map((block, index) => {
					if (block.hero != null) {
						return (
							<div
								key={index}
								className='sa-hero pb-8 md:py-10 xl:py-16'
								{...(pageData.$?.[`content_blocks__${index}`] ?? {})}
							>
								<Container
									width={1440}
									className='grid grid-cols-1 gap-y-6 !pl-0 max-md:!pr-0 md:grid-flow-row md:auto-rows-min md:grid-cols-10 md:gap-x-10 md:gap-y-0 xl:gap-x-16'
								>
									{isValidArray(block.hero.image) && (
										<div className='col-start-1 row-start-1 md:col-span-6 md:col-start-1 md:row-span-2 md:row-start-1'>
											<Image
												{...(block.hero.$?.image__0 ?? {})}
												data={block.hero.image[0]}
												className='md:aspect-none !block aspect-[3/2] w-full md:aspect-square'
												loading='eager'
											/>
										</div>
									)}

									<div className='col-start-1 row-start-2 px-6 md:col-span-4 md:col-start-7 md:row-start-1 md:px-0 md:py-10'>
										{block.hero.heading && (
											<h1
												className='font-semibold'
												{...(block.hero.$?.heading ?? {})}
											>
												{block.hero.heading}
											</h1>
										)}
										{block.hero.description && (
											<div
												{...(block.hero.$?.description ?? {})}
												dangerouslySetInnerHTML={{ __html: block.hero.description }}
											/>
										)}
									</div>

									{(block.hero.add_rta || block.hero.pre_rta) && (
										<div className='z-2 col-start-1 row-start-4 px-6 md:col-span-5 md:col-start-6 md:row-span-3 md:row-start-2 md:px-0'>
											<div
												id='rtaForm'
												className='bg-neutral-50 p-2'
											>
												{block.hero.heading_with_form && (
													<div className='relative -mb-4 px-4 pt-6 md:-mb-8 md:px-8 md:pt-8'>
														{block.hero.rta_heading && (
															<h2
																className='text-h3'
																{...(block.hero.$?.rta_heading ?? {})}
															>
																{block.hero.rta_heading}
															</h2>
														)}
														{block.hero.rta_description && (
															<p
																className='mb-6'
																{...(block.hero.$?.rta_description ?? {})}
															>
																{block.hero.rta_description}
															</p>
														)}
													</div>
												)}
												<ScheduleConsultation
													showPreRta={!!block.hero.pre_rta && branchId === '760'}
													renderAs='form-only'
													src='ppc'
												/>
											</div>
										</div>
									)}

									{!block.hero.heading_with_form && (
										<div
											data-testid='hero-rta-text'
											className='col-start-1 row-start-3 px-6 md:col-span-4 md:col-start-1 md:row-start-3 md:py-10 md:pl-8 md:pr-0 xl:pl-16'
										>
											{block.hero.rta_heading && (
												<h2
													className='text-h3'
													{...(block.hero.$?.rta_heading ?? {})}
												>
													{block.hero.rta_heading}
												</h2>
											)}
											{block.hero.rta_description && <p {...(block.hero.$?.rta_description ?? {})}>{block.hero.rta_description}</p>}
										</div>
									)}
								</Container>
							</div>
						);
					}

					if (block.columns != null) {
						if (block.columns.columns.find((column) => column.promo) && !isValidArray(data?.getPromotionsByBranchId)) return null;

						return (
							<div
								key={index}
								width={1440}
								className='sa-columns py-8 md:py-10 xl:py-16'
								style={block.columns.background_hex_value ? { backgroundColor: block.columns.background_hex_value } : null}
								data-testid='columns'
								{...(pageData.$?.[`content_blocks__${index}`] ?? {})}
							>
								<Container width={1440}>
									{block.columns.heading && (
										<h2
											className='mb-6 text-center font-semibold xl:mb-10'
											dangerouslySetInnerHTML={{ __html: block.columns.heading }}
											{...(block.columns.$?.heading ?? {})}
										/>
									)}

									{isValidArray(block.columns.columns) && (
										<Row
											align='center'
											justify='center'
											largeGutters
											verticalGutters
										>
											{block.columns.columns.map((column, index) => (
												<Column
													xs={12}
													lg={column.column_desktop}
													key={index}
												>
													<div
														className='h-full'
														style={column.container_hex_value ? { backgroundColor: column.container_hex_value } : null}
													>
														{column.promo && isValidArray(data?.getPromotionsByBranchId) && (
															<div className='sa-promo'>
																<div className='bg-primary px-6 py-3 text-center'>
																	<p className='text-subtitle-2 m-0'>Offer Expires In</p>
																	{isClient && (
																		<Countdown
																			zeroPadTime={0}
																			date={new Date(data.getPromotionsByBranchId[0].endDate)}
																			renderer={({ formatted }) => (
																				<p className='text-h2 m-0 font-bold'>
																					{formatted.days > 0 ? `${formatted.days} Days ` : ``}
																					{formatted.hours} Hours {formatted.minutes} Minutes
																				</p>
																			)}
																		/>
																	)}
																</div>

																<BranchOfferText
																	offer={data.getPromotionsByBranchId[0]}
																	className='space-y-4 py-6 text-center [&>*:nth-child(odd)]:text-h3 md:py-10 [&>*]:text-black'
																/>
															</div>
														)}

														{isValidArray(column.image) && (
															<div className='text-center'>
																<Image
																	{...(column.$?.image__0 ?? {})}
																	data={column.image[0]}
																/>
															</div>
														)}
														{column.file?.url && (
															<Image
																{...(column.$?.file ?? {})}
																data={{ image: column.file }}
															/>
														)}

														{column.heading && (
															<h3
																className='mb-6 xl:mb-10'
																{...(column.$?.heading ?? {})}
															>
																{column.heading}
															</h3>
														)}
														{column.description && (
															<div
																{...(column.$?.description ?? {})}
																dangerouslySetInnerHTML={{ __html: column.description }}
															/>
														)}

														{isValidArray(column.cta) && (
															<div className={column.promo ? 'text-center' : ''}>
																<CTA
																	{...(column.$?.cta__0 ?? {})}
																	data={column.cta[0]}
																	className={`mt-6 ${!column.promo ? 'xl:mt-10' : ''}`}
																/>
															</div>
														)}
													</div>
												</Column>
											))}
										</Row>
									)}
								</Container>
							</div>
						);
					}

					if (block.manual_reviews != null) {
						const textColor = getContrastColor(block.manual_reviews.background_hex_value);

						return (
							<div
								key={index}
								className='sa-reviews py-8 md:py-10 xl:py-16'
								style={block.manual_reviews.background_hex_value ? { backgroundColor: block.manual_reviews.background_hex_value } : null}
								data-testid='manual-reviews'
								{...(pageData.$?.[`content_blocks__${index}`] ?? {})}
							>
								<Container width={1440}>
									{block.manual_reviews.heading && (
										<h2
											className={`text-center ${textColor}`}
											{...(block.manual_reviews.$?.heading ?? {})}
											dangerouslySetInnerHTML={{ __html: block.manual_reviews.heading }}
										/>
									)}
									{block.manual_reviews.description && (
										<div className={`mx-auto max-w-screen-md text-pretty text-center ${textColor}`}>
											<span>{branchData?.formal_name || 'Pella'}&nbsp;</span>
											<span
												className='[&>*]:inline'
												{...(block.manual_reviews.$?.description ?? {})}
												dangerouslySetInnerHTML={{ __html: block.manual_reviews.description }}
											/>
										</div>
									)}
								</Container>

								<Container
									width={1440}
									noPadding
								>
									{isValidArray(block.manual_reviews.cards) && (
										<Carousel
											wrapperClassName='mt-6 xl:mt-10'
											className='before:content-none after:content-none'
											dotsClassName='md:hidden [&_[aria-current=true]]:before:bg-primary [&_[aria-current=false]]:opacity-50'
											slidesToShow={{ xs: 1, sm: 2, lg: 3 }}
											slidePadding={20}
											showSlideHints
											showDots
										>
											{block.manual_reviews.cards.map((card, index) => {
												const quoteTextColor = getContrastColor(card.container_hex_value);

												return (
													<div
														key={index}
														className='flex h-full flex-col items-start p-6 md:p-10'
														style={card.container_hex_value ? { backgroundColor: card.container_hex_value } : null}
														data-testid='manual-cards'
													>
														<img
															src='https://images.contentstack.io/v3/assets/bltf589e66bcaecd79c/bltfffffcf41e9dba0f/5e5ebdc77365696430378353/quote-left-solid.svg'
															className='mb-4 h-9'
															alt=''
														/>

														{card.quote && (
															<div
																className={quoteTextColor}
																{...(card.$?.quote ?? {})}
																dangerouslySetInnerHTML={{ __html: card.quote }}
															/>
														)}
														{card.citation && (
															<cite
																className={`${quoteTextColor} mt-auto pt-6 not-italic before:content-["-_"]`}
																{...(card.$?.citation ?? {})}
																dangerouslySetInnerHTML={{ __html: card.citation }}
															/>
														)}
													</div>
												);
											})}
										</Carousel>
									)}
								</Container>
							</div>
						);
					}

					if (isValidArray(block.window_101?.carousel)) {
						return (
							<div
								key={index}
								className='sa-carousel py-8 md:py-10 xl:py-16'
								style={block.window_101.background_hex_value ? { backgroundColor: block.window_101.background_hex_value } : null}
								data-testid='window-101'
								{...(pageData.$?.[`content_blocks__${index}`] ?? {})}
							>
								<Container width={1440}>
									{block.window_101.heading && (
										<h2
											{...(block.window_101.$?.heading ?? {})}
											className='text-center font-semibold [&+p]:mb-3'
										>
											{block.window_101.heading}
										</h2>
									)}
									{block.window_101.subheading && (
										<p
											{...(block.window_101.$?.subheading ?? {})}
											className='text-center'
										>
											{block.window_101.subheading}
										</p>
									)}

									{isValidArray(block.window_101.carousel) && (
										<ProductStyleTabs
											className='mt-6 xl:mt-10'
											{...(block.window_101.$?.carousel ?? {})}
											data={block.window_101.carousel}
											showCobs
										/>
									)}

									{isValidArray(block.window_101.cta) && (
										<div className='mt-6 text-center xl:mt-10'>
											<CTA
												{...(block.window_101.$?.cta__0 ?? {})}
												data={block.window_101.cta[0]}
											/>
										</div>
									)}
								</Container>
							</div>
						);
					}

					if (block.local_content != null && block.local_content.local_content_type === 'nearby' && showrooms?.length > 0) {
						return (
							<div
								key={index}
								{...(pageData.$?.[`content_blocks__${index}`] ?? {})}
								className='sa-nearby py-8 md:py-10 xl:py-16'
							>
								<Container width={1440}>
									{block.local_content.heading && (
										<h2
											className='text-h3 mb-3'
											{...(block.local_content.$?.heading ?? {})}
											dangerouslySetInnerHTML={{ __html: block.local_content.heading }}
										/>
									)}

									<NearbyShowrooms
										locations={showrooms.filter((showroom) => (branchData?.[0]?.by_state ? showroom.state === branchData?.[0]?.state?.abbreviation : true))}
										error={error}
									/>
								</Container>
							</div>
						);
					}
				})}

			<footer className='sa-footer bg-neutral-800 py-8 md:py-10 xl:py-16'>
				<Container className='grid grid-cols-1 items-center gap-4 md:grid-cols-[auto_1fr] md:gap-x-16'>
					<Link to='/'>
						<Logo className='h-14 md:h-20' />
					</Link>

					<div className='col-start-1 md:col-start-2'>
						{data?.getPromotionsByBranchId?.[0]?.disclaimer1 && (
							<div
								className='text-body-2 text-white before:mr-1 before:align-super before:text-[75%] before:content-["1"]'
								dangerouslySetInnerHTML={{ __html: data?.getPromotionsByBranchId?.[0]?.disclaimer1 }}
							/>
						)}

						{data?.getPromotionsByBranchId?.[0]?.disclaimer2 && checkDisclaimerDuplicates(data?.getPromotionsByBranchId?.[0]?.disclaimer2) && (
							<div
								className='text-body-2 text-white before:mr-1 before:align-super before:text-[75%] before:content-["2"]'
								dangerouslySetInnerHTML={{ __html: data?.getPromotionsByBranchId?.[0]?.disclaimer2 }}
							/>
						)}

						{data?.getPromotionsByBranchId?.[0]?.disclaimer3 && checkDisclaimerDuplicates(data?.getPromotionsByBranchId?.[0]?.disclaimer3, 3) && (
							<div
								className={`text-body-2 text-white before:mr-1 before:align-super before:text-[75%] ${data?.getPromotionsByBranchId?.[0]?.disclaimer1 === data?.getPromotionsByBranchId?.[0]?.disclaimer2 ? 'before:content-["2"]' : 'before:content-["3"]'}`}
								dangerouslySetInnerHTML={{ __html: data?.getPromotionsByBranchId?.[0]?.disclaimer3 }}
							/>
						)}

						{pageData.footer_disclaimer && (
							<div
								className='text-body-2 text-white'
								{...(pageData.$?.footer_disclaimer ?? {})}
								dangerouslySetInnerHTML={{ __html: pageData.footer_disclaimer }}
							/>
						)}

						<p
							className='text-body-2 text-white'
							{...(pageData.$?.copyright ?? {})}
						>
							&#169; {isClient && new Date().getFullYear()} {pageData.copyright}
						</p>
					</div>
				</Container>
			</footer>
		</div>
	);
};

SAPaidLanding.propTypes = {
	data: PropTypes.object.isRequired,
};

export default SAPaidLanding;

// eslint-disable-next-line react/prop-types -- gatsby component
export const Head = ({ data }) => <SEO {...data.contentstackSaPaidLanding.seo} />;

export const query = graphql`
	query ($uid: String!) {
		contentstackSaPaidLanding(id: { eq: $uid }) {
			__typename
			uid
			url
			page_heading
			modular_blocks {
				hero {
					image {
						...Image
						image {
							url
							gatsbyImageData(quality: 90)
						}
					}
					heading
					description
					cta {
						...CTA
					}
					add_rta
					pre_rta
					heading_with_form
					rta_heading
					rta_description
				}
				columns {
					background_hex_value
					heading
					columns {
						column_desktop
						container_hex_value
						promo
						image {
							...Image
							image {
								url
								gatsbyImageData(quality: 90)
							}
						}
						file {
							url
							gatsbyImageData(quality: 90)
						}
						heading
						description
						cta {
							...CTA
						}
					}
				}
				manual_reviews {
					background_hex_value
					heading
					description
					cards {
						container_hex_value
						quote
						citation
					}
				}
				window_101 {
					background_hex_value
					heading
					carousel {
						cob {
							url
							gatsbyImageData(quality: 90)
						}
						heading
						content {
							image {
								...Image
								image {
									url
									gatsbyImageData(quality: 90)
								}
							}
							heading
							description
							cta {
								...CTA
							}
						}
					}
					cta {
						...CTA
					}
				}
				local_content {
					local_content_type
					by_state
					heading
					subheading
					description
				}
			}
			footer_disclaimer
			copyright
			seo {
				...SEO
			}
		}
	}
`;
